import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { DataTable, type DataTableColumn, type DataTableSort } from "@/components/cinch/DataTable/DataTable";
import { DateRangePicker, type DateRange } from "@/components/cinch/DatePicker";
import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import {
  CheckboxList,
  FilterBar,
  FilterChip,
  FilterClearAll,
  FilterSearch,
} from "@/components/cinch/FilterBar";
import { cn } from "@/lib/utils";

import { useContractsRecord } from "../useContractsRecord";
import { RecordLink } from "./RecordLink";
import {
  monthLabel,
  useBookedNotBilledDetail,
  type BookedNotBilledAccount,
  type KpiBookedNotBilledDetail,
} from "../api";
import type { KpiSearch } from "..";
import { fmtArr, fmtUsd0 } from "../format";

/** Amber past 30 days waiting, red past 60 — a booked contract that hasn't
 *  started billing is an onboarding that's stalling. */
function waitingTone(days: number | null): string {
  if (days == null) return "text-muted-foreground";
  if (days >= 60) return "text-danger";
  if (days >= 30) return "text-warning";
  return "text-muted-foreground";
}

const toYmd = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const fmtYmd = (d: Date | null): string => (d ? toYmd(d) : "…");

export function BookedNotBilledDrawer() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/kpi" });
  const open = search.tile === "booked";
  const expanded = !!search.expanded;

  const go = (next: Partial<KpiSearch>) =>
    navigate({ to: "/kpi", search: (prev: KpiSearch) => ({ ...prev, ...next }) });

  const detail = useBookedNotBilledDetail(open);

  return (
    <EntityDrawer
      open={open}
      onOpenChange={(v) => {
        if (!v && !expanded) go({ tile: undefined, expanded: undefined });
      }}
      expanded={expanded}
      onExpandedChange={(exp) => open && go({ expanded: exp ? true : undefined })}
      onClose={() => {
        if (expanded) go({ expanded: undefined });
        else go({ tile: undefined, expanded: undefined });
      }}
      title="Booked, not yet billing"
      subtitle="Signed ≤ 12 mo · onboarding not started"
    >
      {detail.isLoading ? (
        <div className="p-6 text-sm text-muted-foreground">Loading…</div>
      ) : !detail.data?.available ? (
        <div className="p-6 text-sm text-muted-foreground">No data.</div>
      ) : expanded ? (
        <ExpandedView data={detail.data} />
      ) : (
        <QuickView data={detail.data} />
      )}
    </EntityDrawer>
  );
}

function Totals({ data }: { data: KpiBookedNotBilledDetail }) {
  return (
    <div className="border-b border-border p-4">
      <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground/70">
        Booked-not-billed ARR
      </div>
      <div className="mt-1 flex items-baseline gap-3">
        <span className="text-3xl font-extrabold tabular-nums">{fmtArr(data.total_arr ?? 0, 0)}</span>
        <span className="text-sm text-muted-foreground">{data.count ?? 0} accounts</span>
      </div>
      <div className="mt-1 text-[11px] text-muted-foreground">
        Signed contracts (excl. POs) with MRR that have never billed a subscription —
        sorted by longest waiting.
      </div>
    </div>
  );
}

// ─── quick view (compact list) ────────────────────────────────────────

function QuickView({ data }: { data: KpiBookedNotBilledDetail }) {
  const { openContract, openAccount } = useContractsRecord();
  const rows = (data.accounts ?? []).slice(0, 15);
  return (
    <div>
      <Totals data={data} />
      <div className="flex flex-col divide-y divide-border">
        {rows.map((a) => (
          <div key={`${a.qb_id}-${a.contract_id}`} className="flex items-center justify-between gap-3 px-4 py-2">
            <div className="min-w-0">
              <RecordLink
                label={a.account_name}
                onOpen={openRecord(a, openContract, openAccount)}
              />
              <div className="text-[11px] text-muted-foreground">
                {a.signed_at ? monthLabel(a.signed_at.slice(0, 7)) : "—"}
              </div>
            </div>
            <div className="flex flex-shrink-0 items-center gap-3">
              <span className={cn("text-xs font-semibold tabular-nums", waitingTone(a.days_waiting))}>
                {a.days_waiting != null ? `${a.days_waiting}d` : "—"}
              </span>
              <span className="text-sm font-semibold tabular-nums">{fmtUsd0(a.arr)}</span>
            </div>
          </div>
        ))}
      </div>
      {(data.accounts?.length ?? 0) > rows.length && (
        <div className="p-4 text-[11px] text-muted-foreground">
          Showing {rows.length} of {data.accounts?.length} — open the full view to filter &amp; sort.
        </div>
      )}
    </div>
  );
}

// ─── expanded view (DS DataTable + filters) ───────────────────────────

function openRecord(
  a: BookedNotBilledAccount,
  openContract: (id: string | number | null | undefined) => unknown,
  openAccount: (id: string | null | undefined) => unknown,
): (() => void) | undefined {
  if (a.contract_id != null) return () => openContract(a.contract_id);
  if (a.account_id) return () => openAccount(a.account_id);
  return undefined;
}

function ExpandedView({ data }: { data: KpiBookedNotBilledDetail }) {
  const { openContract, openAccount } = useContractsRecord();
  const all = useMemo(() => data.accounts ?? [], [data.accounts]);

  const [sort, setSort] = useState<DataTableSort | null>({ key: "days_waiting", dir: "desc" });
  const [searchText, setSearchText] = useState("");
  const [types, setTypes] = useState<string[]>([]);
  const [range, setRange] = useState<DateRange>({ start: null, end: null });

  const typeOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const a of all) {
      if (a.contract_type) counts.set(a.contract_type, (counts.get(a.contract_type) ?? 0) + 1);
    }
    return [...counts.entries()].map(([value, count]) => ({ value, label: value, count }));
  }, [all]);

  const rows = useMemo(() => {
    let out = all;
    if (types.length) out = out.filter((a) => a.contract_type && types.includes(a.contract_type));
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase();
      out = out.filter((a) => a.account_name.toLowerCase().includes(q));
    }
    if (range.start || range.end) {
      const lo = range.start ? toYmd(range.start) : null;
      const hi = range.end ? toYmd(range.end) : null;
      out = out.filter((a) => {
        const d = a.signed_at?.slice(0, 10);
        if (!d) return false;
        if (lo && d < lo) return false;
        if (hi && d > hi) return false;
        return true;
      });
    }
    if (sort) {
      const { key, dir } = sort;
      const mul = dir === "asc" ? 1 : -1;
      out = [...out].sort((x, y) => cmp(x, y, key) * mul);
    }
    return out;
  }, [all, types, searchText, range, sort]);

  const rangeLabel =
    range.start || range.end ? `${fmtYmd(range.start)} → ${fmtYmd(range.end)}` : undefined;
  const hasActiveFilters = types.length > 0 || !!range.start || !!range.end || !!searchText.trim();
  const clearAll = () => {
    setTypes([]);
    setRange({ start: null, end: null });
    setSearchText("");
  };

  const columns: DataTableColumn<BookedNotBilledAccount>[] = [
    {
      key: "account_name",
      header: "Account",
      sortable: true,
      render: (a) => (
        <RecordLink label={a.account_name} onOpen={openRecord(a, openContract, openAccount)} />
      ),
    },
    { key: "contract_type", header: "Type", sortable: true, render: (a) => a.contract_type ?? "—" },
    {
      key: "signed_at",
      header: "Signed",
      sortable: true,
      render: (a) => (a.signed_at ? monthLabel(a.signed_at.slice(0, 7)) : "—"),
    },
    {
      key: "days_waiting",
      header: "Waiting",
      sortable: true,
      numeric: true,
      render: (a) => (
        <span className={cn("font-semibold tabular-nums", waitingTone(a.days_waiting))}>
          {a.days_waiting != null ? `${a.days_waiting}d` : "—"}
        </span>
      ),
    },
    {
      key: "arr",
      header: "ARR",
      sortable: true,
      numeric: true,
      render: (a) => <span className="tabular-nums">{fmtUsd0(a.arr)}</span>,
    },
  ];

  return (
    <div>
      <Totals data={data} />

      <div className="border-b border-border p-4">
        <FilterBar>
          <FilterSearch
            value={searchText}
            onChange={setSearchText}
            placeholder="Search account"
          />
          {typeOptions.length > 1 && (
            <FilterChip label="Type" count={types.length}>
              <CheckboxList options={typeOptions} selected={types} onChange={setTypes} />
            </FilterChip>
          )}
          <FilterChip label="Signed" valueLabel={rangeLabel}>
            <div className="space-y-2">
              <DateRangePicker value={range} onChange={setRange} placeholder="Pick a range" />
              {(range.start || range.end) && (
                <button
                  type="button"
                  onClick={() => setRange({ start: null, end: null })}
                  className="text-xs text-brand hover:underline focus-visible:underline focus-visible:outline-none"
                >
                  Clear dates
                </button>
              )}
            </div>
          </FilterChip>
          <div className="flex-1" />
          {hasActiveFilters && <FilterClearAll onClick={clearAll} />}
        </FilterBar>
      </div>

      <div className="p-4">
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(a) => `${a.qb_id}-${a.contract_id}`}
          sort={sort}
          onSortChange={setSort}
          onRowClick={(a) => openRecord(a, openContract, openAccount)?.()}
          emptyMessage="No accounts match these filters."
          containerClassName="max-h-[calc(100vh-24rem)]"
        />
        <div className="mt-2 text-[10px] text-muted-foreground">
          {rows.length} of {all.length} accounts · click a row to open its contract record.
        </div>
      </div>
    </div>
  );
}

function cmp(x: BookedNotBilledAccount, y: BookedNotBilledAccount, key: string): number {
  switch (key) {
    case "days_waiting":
      return (x.days_waiting ?? -1) - (y.days_waiting ?? -1);
    case "arr":
      return x.arr - y.arr;
    case "signed_at":
      return (x.signed_at ?? "").localeCompare(y.signed_at ?? "");
    case "contract_type":
      return (x.contract_type ?? "").localeCompare(y.contract_type ?? "");
    default:
      return x.account_name.localeCompare(y.account_name);
  }
}
